<template>
  <div class="vehicle-product-page">
    <VehiclePageHeader
      :item="currentItem"
      :variants="variantItems"
      :repayment.sync="repayment"
      :lease-term.sync="leaseTerm"
      :lease-term-options="leaseTermOptions"
      :placeholder-image-svg="placeholderImageSvg"
      :back-to-search-route="backToSearchRoute"
      :back-nav-label="productPageBackNavLabel"
      :pagination-total="paginationTotal"
      :current-slide="currentSlide"
      :show-variants="showVariants"
      :variants-section-key="variantsSectionKey"
      :has-items-in-short-list="hasItemsInShortList"
      :short-list="shortList"
      @back="onBack"
      @view-shortlist="onViewShortlist"
      @enquire-now="onEnquireNow"
      @shortlist="onShortlist"
      @view-variants="onViewVariants"
      @close-variants="onCloseVariants"
      @select-variant="onSelectVariant"
      @on-view-vehicle="onViewVehicleVariant"
      @on-shortlist="onShortlistVariant"
      @open-mobile-filters="showMobileFiltersDrawer = true"
    />
    <VehicleProductFilterDrawer
      v-model="showMobileFiltersDrawer"
      :repayment="repayment"
      :lease-term="leaseTerm"
      :lease-term-options="leaseTermOptions"
      @leaseInclusion="handleLeaseInclusionDialog"
      @apply="onMobileFiltersApply"
    />
    <LeaseInclusionModal v-model="showLeaseInclusion" />
    <EnquireNowModal v-model="showEnquireNowModal" @onClose="showEnquireNow(false)" :vehicle="currentItem" />

    <VehiclePageOverview
      v-if="currentItem && currentItem.marketingSubject && currentItem.marketingBodyText"
      :item="currentItem"
      :default-overview="defaultOverview"
      @enquire-now="onEnquireNow"
    />
    <VehiclePageSpecifications title="Car specifications" :item="currentItem" />

    <VehiclePageLeaseInclusions />

    <VehiclePageLeaseInfo
      :eyebrow="maintainedLeaseSection.label"
      :title="maintainedLeaseSection.title"
      :description="maintainedLeaseSection.description"
      :image-url="maintainedLeaseSection.imageUrl"
      @enquire-now="onEnquireNow"
    />
    <VehiclePageMedia
      v-if="1 === 0"
      eyebrow="MEDIA"
      :title="mediaSection.title"
      :poster-url="mediaSection.posterUrl"
      :video-id="mediaSection.videoId"
      @play="onMediaPlay"
    />
    <VehiclePageFaq title="Frequently asked questions" :items="faqs" :default-open-index="0" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { DEFAULT_LEASE_TERM, LEASE_TERM_OPTIONS, REPAYMENT_WEEKLY } from '../../utils/constants';
import {
  buildQueryWithBudgetType,
  buildQueryWithLeaseTerm,
  readLeaseTermFromQuery,
  readRepaymentFromQuery
} from '../../utils/calculator/calculatorPageQueryState';
import {
  vehicleVariantDetailsMixin,
  shortListOperationsMixin,
  vehicleResultPageShortlistMixin,
  vehicleResultPageModalMixin
} from '../../mixins/calculator';
import VehiclePageHeader from '../../components/Calculator/VehiclePage/VehiclePageHeader.vue';
import VehicleProductFilterDrawer from '../../components/Calculator/VehiclePage/VehicleProductFilterDrawer.vue';
import LeaseInclusionModal from '../../components/Calculator/LeaseInclusion/LeaseInclusionModal.vue';
import VehiclePageLeaseInclusions from '../../components/Calculator/VehiclePage/VehiclePageLeaseInclusions.vue';
import VehiclePageOverview from '../../components/Calculator/VehiclePage/VehiclePageOverview.vue';
import VehiclePageSpecifications from '../../components/Calculator/VehiclePage/VehiclePageSpecifications.vue';
import VehiclePageLeaseInfo from '../../components/Calculator/VehiclePage/VehiclePageLeaseInfo.vue';
import VehiclePageMedia from '../../components/Calculator/VehiclePage/VehiclePageMedia.vue';
import VehiclePageFaq from '../../components/Calculator/VehiclePage/VehiclePageFaq.vue';
import EnquireNowModal from '../../components/Calculator/Modal/EnquireNowModal.vue';

/** Vue Router may throw or use varying messages for same-location navigations. */
function isRedundantNavigationError(err) {
  if (!err) return false;
  if (err.name === 'NavigationDuplicated') return true;
  return /redundant navigation|navigation duplicated/i.test(String(err.message || ''));
}

const DEFAULT_OVERVIEW = {
  label: 'OVERVIEW',
  title: 'Sporty Design. Serious Performance.',
  description:
    'The Audi Q5 Sportback S line Edition combines a striking coupe-style silhouette with the practicality of an SUV. Designed for versatility, performance and technology, it delivers confident handling and a refined cabin with advanced safety features—ideal for modern business fleets.',
  imageUrl: '/assets/product_details/image_1.png'
};

export default {
  name: 'VehicleProductPage',
  mixins: [vehicleVariantDetailsMixin, shortListOperationsMixin, vehicleResultPageShortlistMixin, vehicleResultPageModalMixin],
  components: {
    VehiclePageHeader,
    VehicleProductFilterDrawer,
    LeaseInclusionModal,
    VehiclePageLeaseInclusions,
    VehiclePageOverview,
    VehiclePageSpecifications,
    VehiclePageLeaseInfo,
    VehiclePageMedia,
    VehiclePageFaq,
    EnquireNowModal
  },
  data() {
    return {
      variantDetailsRaw: null,
      repayment: REPAYMENT_WEEKLY,
      leaseTerm: null,
      leaseTermOptions: LEASE_TERM_OPTIONS,
      placeholderImageSvg: `${process.env.BASE_URL}assets/images/vehicleImagePlaceholder.svg`,
      showVariants: false,
      variantsSectionKey: 0,
      currentSlide: 0,
      showEnquireNowModal: false,
      showMobileFiltersDrawer: false,
      /** Suppress syncLeaseTermQuery/syncRepaymentQuery while applying merged query from the mobile drawer. */
      skipVehicleProductFilterQuerySync: false,
      showLeaseInclusion: false,
      /** Stack of vehicle product routes when user moved variant → variant (LIFO for header Back) */
      productVariantBackStack: [],
      maintainedLeaseSection: {
        label: 'BUSINESS LEASE',
        title: 'Get more with a fully maintained\noperating lease',
        description:
          'With a Fully Maintained Operating Lease, you control your cars and your cash. Secure a new vehicle, with no cash payment upfront. Get clear visibility of costs, with just one monthly budgeted payment covering your lease, scheduled servicing and maintenance. Plus, choose from flexible lease terms, typically 3 to 5 years. Outsource vehicle management and tedious administration to our expert team – giving you the time to focus more on your business.',
        imageUrl: '/assets/product_details/lease_info.png'
      },
      mediaSection: {
        title: 'Audi RS 5',
        videoId: 'a2HmjjS1tm8'
      },
      faqs: [
        {
          question: 'What is a Fully Maintained Operating lease?',
          answer: `A Fully Maintained Operating Lease is like a rental agreement for a vehicle. You lease the vehicle from FleetPartners for a set term, usually between 3 to 5 years. We bundle the majority of your vehicle\'s running costs – into one monthly payment for the duration of your lease term. <br/><br/>
            
            When calculating your monthly payments, we also consider how many kilometres you drive and how long you plan on leasing the vehicle. At the end of the lease, you simply return the vehicle with no residual value or balloon payment.`
        },
        {
          question: 'What are the benefits of an Operating Lease?',
          answer: `Leasing can help free up your business cash, outsource the management of your vehicles to save you time, and keep your costs in check with one monthly payment. <br/><br/>

          Instead of spending your business cash on vehicles, you can invest it in other things that help grow your business. Plus, if you have a small fleet of vehicles, you can outsource paperwork, fuel receipt reconciliation, regular services, maintenance and registration, tolls and infringements –saving yourself hours of admin time.`
        },
        {
          question: 'How accurate is the business lease calculator?',
          answer: `The FleetPartners business lease calculator uses the latest information from vehicle manufacturers and maintenance and servicing providers – it then refines this to meet your search criteria, such as vehicle body and fuel type to provide you with pricing estimates. <br/><br/>

          While the calculator doesn’t provide a specific vehicle quote, the results will provide a great guide which can help you decide which vehicle suits you. Our team can then provide you with an accurate quote on the exact vehicle you’re after. `
        },
        {
          question: 'What happens once I’ve calculated my estimated repayments?',
          answer: `Once you’ve calculated your estimated payments for leasing a business vehicle, you can request a call back from our team. When you’re ready to go ahead, we’ll leverage our supplier network to negotiate a great price on the exact car you want.
          <br/><br/>
          The price of your lease can include accessories like branded car wraps* (which are great for advertising your business) or roof racks, the number of kilometres you travel on average, the lease term, and running costs like servicing, maintenance, and tyres.
          <br/>
          Customised vehicle branding must be removed at your cost before returning vehicles to FleetPartners at the end of lease.`
        },
        {
          question: 'What happens to the vehicles at the end of the lease term?',
          answer:
            'As long as you’ve taken care of the vehicle and the wear and tear is fair, and your kilometres driven in line with the lease agreement, you simply return the vehicle. In some cases, you can also extend the lease term or we can provide you with a quote to replace it with a brand-new vehicle of your choice.'
        }
      ]
    };
  },
  computed: {
    ...mapGetters({
      user: 'auth/user'
    }),
    catalogVehicleId() {
      return this.$route.params.catalogVehicleId;
    },
    /** Items for current vehicle: same catalogVehicleId, one per duration (36/48/60) */
    items() {
      return this.variantDetailsRaw?.items || [];
    },
    /** Current ratebook item matching selected lease term */
    currentItem() {
      const duration = this.leaseTerm?.value;
      if (!duration || !this.items.length) return this.items[0] || null;
      return this.items.find((i) => i.duration === duration) || this.items[0];
    },
    /** One raw ratebook item per other catalog vehicle (for variants carousel) */
    variantItems() {
      const list = this.variantDetailsRaw?.variants || [];
      const duration = this.leaseTerm?.value;
      return this.pickVariantsByCatalogId(list, duration);
    },
    paginationTotal() {
      return Math.max(this.variantItems.length, 1);
    },
    defaultOverview() {
      return DEFAULT_OVERVIEW;
    },
    backToSearchRoute() {
      return { path: '/calculator/results', query: this.$route.query };
    },
    mediaEmbedUrl() {
      return `https://www.youtube.com/embed/${this.mediaSection.videoId}?rel=0`;
    },
    hasItemsInShortList() {
      return (this.shortlist || []).length > 0;
    },
    shortList() {
      return this.shortlist || [];
    },
    productPageBackNavLabel() {
      return this.productVariantBackStack.length > 0 ? 'Back' : 'Back to search';
    }
  },
  created() {
    this.initializeLeaseTerm();
    this.initializeRepayment();
  },
  watch: {
    'catalogVehicleId': {
      handler(id) {
        if (id) this.loadVariantDetails();
      },
      immediate: false
    },
    'leaseTerm': {
      handler(value) {
        this.syncLeaseTermQuery(value);
      },
      deep: true
    },
    '$route.query.leaseTerm': {
      handler() {
        this.initializeLeaseTerm();
      }
    },
    'repayment'(value) {
      this.syncRepaymentQuery(value);
    },
    '$route.query.budgetType': {
      handler() {
        this.initializeRepayment();
      }
    }
  },
  async mounted() {
    await this.loadVariantDetails();
  },
  methods: {
    initializeLeaseTerm() {
      this.leaseTerm = readLeaseTermFromQuery(this.$route.query, this.leaseTermOptions, DEFAULT_LEASE_TERM);
    },
    initializeRepayment() {
      this.repayment = readRepaymentFromQuery(this.$route.query);
    },
    syncLeaseTermQuery(value) {
      if (this.skipVehicleProductFilterQuerySync) return;

      const nextQuery = buildQueryWithLeaseTerm(this.$route.query, value, this.leaseTermOptions, DEFAULT_LEASE_TERM);

      if (!nextQuery) return;

      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query: nextQuery
      });
    },
    syncRepaymentQuery(value) {
      if (this.skipVehicleProductFilterQuerySync) return;

      const nextQuery = buildQueryWithBudgetType(this.$route.query, value);

      if (!nextQuery) return;

      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query: nextQuery
      });
    },
    async loadVariantDetails() {
      const requestedId = this.catalogVehicleId;
      const response = await this.fetchVehicleVariantDetails(requestedId);
      if (this.catalogVehicleId !== requestedId) return;
      this.variantDetailsRaw = response || null;
      if (this.variantDetailsRaw?.items?.length && this.leaseTerm) {
        const items = this.variantDetailsRaw.items;
        const hasDuration = items.some((i) => i.duration === this.leaseTerm.value);
        if (!hasDuration) {
          const firstDuration = items[0].duration;
          this.leaseTerm = this.leaseTermOptions.find((opt) => opt.value === firstDuration) || this.leaseTerm;
        }
      }
    },
    onBack() {
      const previous = this.productVariantBackStack.pop();
      if (previous) {
        this.$router.push(previous);
      } else {
        this.$router.push(this.backToSearchRoute);
      }
    },
    onViewShortlist() {
      if (!this.user) {
        this.$router.push({
          name: 'calculatorShortlists',
          query: {
            ...(this.$route.query || {})
          }
        });
        return;
      }
      this.$router.push({ name: 'dashboard' });
    },
    showEnquireNow(value, vehicle) {
      return this.vrpModal_showEnquireNow(value, vehicle);
    },
    onEnquireNow() {
      return this.showEnquireNow(true, this.currentItem);
    },
    handleLeaseInclusionDialog() {
      return this.vrpModal_handleLeaseInclusionDialog();
    },
    /**
     * Apply mobile drawer lease term + repayment in one navigation (no draft → route churn while toggling).
     */
    async onMobileFiltersApply({ repayment, leaseTerm }) {
      this.skipVehicleProductFilterQuerySync = true;
      try {
        let resolvedLeaseTerm = leaseTerm;
        const items = this.variantDetailsRaw?.items || [];
        if (items.length && resolvedLeaseTerm) {
          const hasDuration = items.some((i) => i.duration === resolvedLeaseTerm.value);
          if (!hasDuration) {
            const firstDuration = items[0].duration;
            resolvedLeaseTerm = this.leaseTermOptions.find((opt) => opt.value === firstDuration) || resolvedLeaseTerm;
          }
        }

        let nextQuery = { ...(this.$route.query || {}) };
        const qLease = buildQueryWithLeaseTerm(nextQuery, resolvedLeaseTerm, this.leaseTermOptions, DEFAULT_LEASE_TERM);
        if (qLease) nextQuery = qLease;
        const qBudget = buildQueryWithBudgetType(nextQuery, repayment);
        if (qBudget) nextQuery = qBudget;

        try {
          await this.$router.replace({
            name: this.$route.name,
            params: this.$route.params,
            query: nextQuery
          });
        } catch (e) {
          if (!isRedundantNavigationError(e)) throw e;
        }

        this.repayment = repayment;
        this.leaseTerm = resolvedLeaseTerm;
      } finally {
        await this.$nextTick();
        this.skipVehicleProductFilterQuerySync = false;
      }
    },
    onShortlist() {
      const isCurrentlyShortlisted = this.shortList.some((item) => item.catalogVehicleId === this.currentItem?.catalogVehicleId);
      return this.vrpShortlist_handleInput({
        ...this.currentItem,
        isShortlisted: !isCurrentlyShortlisted
      });
    },
    onRequestQuote() {},
    onMediaPlay() {
      if (this.mediaSection.videoId) {
        window.open(`https://www.youtube.com/watch?v=${this.mediaSection.videoId}`, '_blank');
      }
    },
    onViewVariants() {
      this.showVariants = true;
      this.variantsSectionKey += 1;
    },
    onCloseVariants() {
      this.showVariants = false;
    },
    onViewVehicleVariant(variant) {
      this.onSelectVariant(variant);
    },
    cloneCurrentVehicleProductLocation() {
      return {
        name: 'vehicleProduct',
        params: { ...this.$route.params },
        query: { ...(this.$route.query || {}) }
      };
    },
    onSelectVariant(variant) {
      this.showVariants = false;
      if (!variant?.catalogVehicleId || variant.catalogVehicleId === this.catalogVehicleId) return;
      this.productVariantBackStack.push(this.cloneCurrentVehicleProductLocation());
      this.$router.push({
        name: 'vehicleProduct',
        params: {
          catalogVehicleId: variant.catalogVehicleId,
          vehicleMake: variant.vehicleMake,
          vehicleModel: variant.vehicleModel,
          vehicleTrim: variant.vehicleTrim ? variant.vehicleTrim.toUpperCase() : undefined,
          vehicleDrivetrain: variant.vehicleDrivetrain ? variant.vehicleDrivetrain.toUpperCase() : undefined
        },
        query: {
          ...this.$route.query
        }
      });
    },
    onShortlistVariant(variant) {
      if (!variant) return;
      const isCurrentlyShortlisted = this.shortList.some((item) => item.catalogVehicleId === variant.catalogVehicleId);
      return this.vrpShortlist_handleInput({
        ...variant,
        isShortlisted: !isCurrentlyShortlisted
      });
    }
  }
};
</script>
